<template>
  <v-card flat>
    <FormField label="Nome da Forma de Entrega" v-model="form.nomeForma" />
    <h4 class="text-center">Credenciais</h4>
    <LineDivider></LineDivider>
    <FormField label="API ID" v-model="form.shippify.apiId" />
    <FormField label="API Token" v-model="form.shippify.apiToken" />
    <FormField label="Company ID" v-model="form.shippify.companyId" />

    <h4 class="text-center">Informações de Coleta - Contato</h4>
    <LineDivider></LineDivider>
    <FormField
      label="Nome do responsável"
      v-model="form.shippify.pickupContactName"
    />
    <FormField
      label="E-mail do responsável"
      v-model="form.shippify.pickupContactEmail"
      typeField="email"
    />
    <FormField
      label="Telefone do responsável (opcional)"
      v-model="form.shippify.pickupContactPhone"
    />

    <h4 class="text-center">Informações de Coleta - Localização</h4>
    <LineDivider></LineDivider>
    <FormField
      label="Endereço completo"
      v-model="form.shippify.pickupAddress"
    />
    <FormField
      label="Instruções extras (opcional)"
      v-model="form.shippify.pickupInstructions"
      hint="Informações extras sobre o endereço"
    />
    <FormField
      label="Latitude (opcional)"
      v-model="form.shippify.pickupLat"
      typeField="number"
      :step="'any'"
      help="Coordenada de latitude do ponto de coleta. Aceita valores decimais e negativos, por exemplo -23.550520."
    />
    <FormField
      label="Longitude (opcional)"
      v-model="form.shippify.pickupLng"
      typeField="number"
      :step="'any'"
      help="Coordenada de longitude do ponto de coleta. Aceita valores decimais e negativos, por exemplo -46.633308."
    />

    <h4 class="text-center">Configurações Gerais</h4>
    <LineDivider></LineDivider>
    <FormField
      label="Custo de separação"
      :money="form.shippify.acrescimoTaxa === null ? null : moneyMask"
      v-model="form.shippify.acrescimoTaxa"
    />
    <FormField
      label="Tempo de preparo e despacho em dias"
      v-model="form.shippify.diasSeparacao"
      typeField="number"
    />
    <FormAutocomplete
      label="Tipo da cotação"
      v-model="form.shippify.tipoCotacao"
      :options="opcoesTipoCotacao"
      class="mt-3"
    />
    <FormField
      class="mt-3"
      label="Valor mínimo do frete grátis"
      v-model="form.shippify.valorMinimoFreteGratis"
      typeField="number"
      :step="'0.01'"
      help="Pedidos com valor total igual ou superior ao informado ficam isentos do frete. Deixe vazio para não oferecer essa opção; informe 0 para permitir frete grátis em qualquer valor de pedido."
    />
    <FormField
      class="mt-3"
      label="Raio de frete grátis (km)"
      v-model="form.shippify.raioFreteGratis"
      typeField="number"
      :step="'0.1'"
      help="Clientes dentro do raio informado não pagam frete; a empresa continua pagando normalmente a Shippify. A distância é calculada em linha reta entre a coleta e o endereço do cliente. Deixe vazio para não oferecer frete grátis por raio. Quando o valor mínimo também estiver configurado, o frete só fica grátis se os dois critérios forem atendidos."
    />
    <FormField
      class="mt-3"
      label="Nome do frete grátis"
      v-model="form.shippify.nomeFreteGratis"
      :disabled="!freteGratisDefinido"
      help="Nome exibido na opção de entrega sem custo. Se ficar em branco, será usado um nome padrão."
    />

    <h4 class="text-center mt-5">Configurações de Dimensões de Pacotes</h4>
    <LineDivider></LineDivider>

    <div v-for="(tamanho, index) in tamanhosPacotes" :key="index" class="mb-4">
      <FormField
        :label="`Volume (m³) - ${tamanho.nome}`"
        v-model="form.shippify.dimensoesPacotes[tamanho.codigo].volume"
        typeField="number"
        :step="'0.0001'"
        :help="tamanho.descricao"
      />
    </div>
  </v-card>
</template>

<script>
import FormField from "@/components/form/FormField";
import FormAutocomplete from "@/components/form/FormAutocomplete";
import LineDivider from "@/components/LineDivider";

export default {
  name: "FormaEntregaFormShippify",
  components: {
    FormField,
    FormAutocomplete,
    LineDivider
  },
  props: {
    data: {
      type: [Object],
      default: () => ({}),
      required: true
    }
  },
  data() {
    return {
      form: {},
      opcoesTipoCotacao: [
        { value: "flex", text: "Flex" },
        { value: "express", text: "Express" },
        { value: "slot", text: "Slot" }
      ],
      moneyMask: {
        decimal: ",",
        thousands: " ",
        prefix: "",
        suffix: "",
        default: "",
        precision: 2,
        masked: false
      },
      tamanhosPacotes: [
        {
          codigo: "extraPequeno",
          nome: "Extra Pequeno",
          descricao: "Uma caixa que você consegue segurar com uma mão.",
          volume: 0.0017
        },
        {
          codigo: "pequeno",
          nome: "Pequeno",
          descricao: "Aproximadamente o tamanho de uma caixa de sapato.",
          volume: 0.0078
        },
        {
          codigo: "medio",
          nome: "Médio",
          descricao: "Uma caixa da largura de um braço aberto.",
          volume: 0.0553
        },
        {
          codigo: "grande",
          nome: "Grande",
          descricao: "Uma caixa tão alta quanto a cintura.",
          volume: 0.2074
        },
        {
          codigo: "extraGrande",
          nome: "Extra Grande",
          descricao: "Caixas maiores que dois braços abertos.",
          volume: 0.4977
        }
      ]
    };
  },
  computed: {
    freteGratisDefinido() {
      const preenchido = v => !(v === null || v === undefined || v === "");
      return (
        preenchido(this.form?.shippify?.valorMinimoFreteGratis) ||
        preenchido(this.form?.shippify?.raioFreteGratis)
      );
    }
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.$emit("update", this.form);
      }
    },
    freteGratisDefinido(definido) {
      if (!definido) {
        this.$set(this.form.shippify, "nomeFreteGratis", "");
      }
    }
  },
  created() {
    this.form = this.data;
    this.inicializarDimensoesPacotes();
  },
  methods: {
    inicializarDimensoesPacotes() {
      if (!this.form.shippify) {
        this.$set(this.form, "shippify", {});
      }
      if (!this.form.shippify.dimensoesPacotes) {
        this.$set(this.form.shippify, "dimensoesPacotes", {});
      }

      this.tamanhosPacotes.forEach(tamanho => {
        if (!this.form.shippify.dimensoesPacotes[tamanho.codigo]) {
          this.$set(this.form.shippify.dimensoesPacotes, tamanho.codigo, {
            volume: tamanho.volume
          });
        }
      });
    }
  }
};
</script>
